// Client for the self-hosted signage API (server/signage-api.mjs), which
// replaced Supabase. Same origin as the player, so no CORS and no keys: the
// player only ever reads, and reads are public — exactly what the Supabase
// anon key allowed.

/**
 * Locations added from the panel live under /loc/<name>/ and share one build
 * of the player, so there the API address comes from the page's own address.
 * A build for a fixed location (Buchanan Galleries, Wishaw) sets VITE_API_BASE.
 */
const LOCATION_PAGE = window.location.pathname.match(/^\/loc\/([a-z0-9-]+)\//);

/** e.g. https://americandirtysoda.co.uk/signage-api — no trailing slash. */
export const API_BASE = (
  import.meta.env.VITE_API_BASE ||
  (LOCATION_PAGE ? `${window.location.origin}/loc/${LOCATION_PAGE[1]}/api` : `${window.location.origin}/signage-api`)
).replace(/\/+$/, "");

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { cache: "no-store" });
  if (!res.ok) throw new Error(`GET ${path} failed: HTTP ${res.status}`);
  return (await res.json()) as T;
}

type Listener = (data: unknown) => void;

/**
 * One EventSource per screen, shared by the config and command hooks, with
 * listeners for "config" and "command" events and an "open" hook that fires on
 * every (re)connect — the moment to re-fetch anything missed while the stream
 * was down. EventSource reconnects on its own (the server sets retry: 5000).
 */
class ScreenStream {
  private source: EventSource | null = null;
  private listeners = new Map<string, Set<Listener>>();
  private openListeners = new Set<() => void>();
  private refs = 0;

  constructor(private readonly screenId: string) {}

  on(event: "config" | "command", fn: Listener) {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(fn);
    this.source?.addEventListener(event, this.dispatch);
    return () => this.listeners.get(event)?.delete(fn);
  }

  onOpen(fn: () => void) {
    this.openListeners.add(fn);
    return () => this.openListeners.delete(fn);
  }

  acquire() {
    if (this.refs++ === 0) this.connect();
  }

  release() {
    if (--this.refs === 0) {
      this.source?.close();
      this.source = null;
    }
  }

  private dispatch = (e: Event) => {
    const msg = e as MessageEvent<string>;
    let data: unknown;
    try {
      data = JSON.parse(msg.data);
    } catch {
      return;
    }
    for (const fn of this.listeners.get(msg.type) ?? []) fn(data);
  };

  private connect() {
    if (typeof EventSource === "undefined") return; // the config watchdog still polls
    const source = new EventSource(`${API_BASE}/events?screen=${encodeURIComponent(this.screenId)}`);
    source.addEventListener("config", this.dispatch);
    source.addEventListener("command", this.dispatch);
    source.addEventListener("open", () => {
      for (const fn of this.openListeners) fn();
    });
    this.source = source;
  }
}

const streams = new Map<string, ScreenStream>();

export function screenStream(screenId: string): ScreenStream {
  let s = streams.get(screenId);
  if (!s) {
    s = new ScreenStream(screenId);
    streams.set(screenId, s);
  }
  return s;
}

/**
 * Is the API actually reachable right now?
 *
 * navigator.onLine only reflects the network interface, not the path to the
 * internet — it can say "online" while the connection is genuinely unusable.
 * A screen that trusted it navigated away for a scheduled reload during a
 * dip, landed on the browser's own error page instead of this app, and sat
 * there for 14 hours: nothing runs on that page to retry or report it.
 */
export async function isApiReachable(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/health`, { cache: "no-store" });
    return res.ok;
  } catch {
    return false;
  }
}
